/**
 * LXRYWOOD — LP Akustikpaneele Cinematic Hero Section JS
 * Scroll-driven cinematic transitions between N scenes
 * Spec: docs/landing-page-engine/cinematic-hero-skill.md
 *
 * Per-Scene Config via CSS-Custom-Properties auf <section.lxry-cinema__scene>.
 * Transition-Dispatch auf 6 Typen (zoom-through, crossfade, slide-up,
 * slide-left, scale-down, none). Piecewise per-scene scene_vh (Summe statt
 * uniform). Flash + Grain sind nur während zoom-through aktiv und werden
 * zusätzlich per-scene via data-flash / data-grain gated.
 */
(function () {
  'use strict';

  var clamp = function (v, min, max) {
    return Math.max(min, Math.min(max, v));
  };

  var easeInOut = function (t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  };

  function easeFn(name, t) {
    switch (name) {
      case 'linear':
        return t;
      case 'easeInOut':
        return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      case 'easeInEaseOutBack':
        return t < 0.5
          ? (Math.pow(2 * t, 2) * 1.5) / 2
          : 1 - Math.pow(-2 * t + 2, 3) / 2;
      case 'easeInCubic':
      default:
        return t * t * t;
    }
  }

  function readSceneConfig(sceneEl) {
    var s = getComputedStyle(sceneEl);
    var num = function (name, fallback) {
      var v = parseFloat(s.getPropertyValue(name));
      return isNaN(v) ? fallback : v;
    };
    return {
      // numerisch (CSS-Custom-Properties — Timing-% kommen als Integer rein,
      // hier auf 0..1 normalisiert → lessons-learned §20)
      sceneVh: num('--scene-vh', 110),
      zoom: num('--scene-zoom', 2.6),
      initZoom: num('--scene-img-initial-zoom', 1.02),
      blurOut: num('--scene-blur-out', 12),
      darkenOut: num('--scene-darken-out', 30) / 100,
      tOutStart: num('--scene-t-out-start', 0) / 100,
      tOutEnd: num('--scene-t-out-end', 35) / 100,
      tZoomEnd: num('--scene-t-zoom-end', 65) / 100,
      tNextStart: num('--scene-t-next-in-start', 45) / 100,
      tNextEnd: num('--scene-t-next-in-end', 100) / 100,
      entranceDelay: num('--scene-entrance-delay', 0),
      entranceDuration: num('--scene-entrance-duration', 600),
      entranceStagger: num('--scene-entrance-stagger', 100),
      // nicht-numerisch (data-Attribute, mit Defaults)
      transition: sceneEl.dataset.transition || 'zoom-through',
      easeCurve: sceneEl.dataset.ease || 'easeInCubic',
      entrance: sceneEl.dataset.entrance || 'fade-up',
      stagger: sceneEl.dataset.stagger !== 'false',
      flash: sceneEl.dataset.flash !== 'false',
      grain: sceneEl.dataset.grain !== 'false'
    };
  }

  function initCinemaHero(stack) {
    if (!stack || stack.__lxryCinemaInit) return;
    stack.__lxryCinemaInit = true;

    var sectionEl = stack.closest('.shopify-section') || document.body;
    var scenes = Array.prototype.slice.call(
      stack.querySelectorAll('.lxry-cinema__scene')
    );
    var sceneCount = scenes.length;
    if (!sceneCount) {
      stack.__lxryCinemaInit = false;
      return;
    }

    var flash = sectionEl.querySelector('.lxry-cinema__flash');
    var grainLines = sectionEl.querySelector('.lxry-cinema__grain');
    var sceneNav = sectionEl.querySelector(
      '.lxry-cinema__nav[data-lxry-cinema-role="scene-nav"]'
    );
    var stickySample = sectionEl.querySelector(
      '.lxry-cinema__sticky[data-lxry-cinema-role="sticky-sample"]'
    );

    // Per-scene config + cumulative vh offsets — neu berechnet bei resize
    var configs = [];
    var sceneOffsetsVh = [];
    var totalVh = 0;
    var entered = { 0: true }; // Szene 0 ist beim Laden bereits sichtbar

    function recompute() {
      configs = scenes.map(readSceneConfig);
      sceneOffsetsVh = [];
      totalVh = 0;
      for (var i = 0; i < sceneCount; i++) {
        sceneOffsetsVh.push(totalVh);
        totalVh += configs[i].sceneVh;
      }
      stack.style.height = totalVh + 100 + 'vh';
    }

    recompute();

    // Scene-Nav Dots: dynamisch aus Szenen erzeugen
    var dots = [];
    if (sceneNav) {
      sceneNav.innerHTML = '';
      scenes.forEach(function (_, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'lxry-cinema__nav-dot';
        dot.setAttribute('aria-label', 'Szene ' + (i + 1));
        dot.addEventListener('click', function () {
          scrollToScene(i);
        });
        sceneNav.appendChild(dot);
        dots.push(dot);
      });
    }

    function scrollToScene(i) {
      var stackTop = stack.getBoundingClientRect().top + window.scrollY;
      var offsetPx = (sceneOffsetsVh[i] || 0) * window.innerHeight / 100;
      window.scrollTo({ top: stackTop + offsetPx, behavior: 'smooth' });
    }

    // Entrance-Animation für eine Szene — Staggered pro Element, wenn
    // data-stagger="true". Null-safe für Atem-Shots ohne .lxry-cinema__content.
    function runEntrance(sceneEl, cfg) {
      var content = sceneEl.querySelector('.lxry-cinema__content');
      if (!content) return;
      var cs = getComputedStyle(sceneEl);
      var fx = cs.getPropertyValue('--entrance-from-x').trim() || '0';
      var fy = cs.getPropertyValue('--entrance-from-y').trim() || '0';
      var fs = parseFloat(cs.getPropertyValue('--entrance-from-scale')) || 1;

      var items = cfg.stagger
        ? content.querySelectorAll(
            '.lxry-cinema__label, .lxry-cinema__title, .lxry-cinema__sub, .lxry-cinema__cta'
          )
        : [content];
      var list = Array.prototype.slice.call(items);
      if (!list.length) list = [content];

      list.forEach(function (el, idx) {
        el.style.opacity = '0';
        el.style.transform =
          'translate(' + fx + ',' + fy + ') scale(' + fs + ')';
        el.style.transition =
          'opacity ' +
          cfg.entranceDuration +
          'ms ease-out, transform ' +
          cfg.entranceDuration +
          'ms ease-out';
        el.style.transitionDelay =
          cfg.entranceDelay + idx * cfg.entranceStagger + 'ms';
        requestAnimationFrame(function () {
          el.style.opacity = '1';
          el.style.transform = 'translate(0,0) scale(1)';
        });
      });
    }

    // Szene, die aktuell "nicht dran ist" — setzt sichtbaren Endzustand
    // (past = schon durchgescrollt) oder Anfangszustand (future = kommt noch).
    function resetScene(sceneEl, state) {
      var cfg = configs[scenes.indexOf(sceneEl)];
      if (!cfg) return;
      var bg = sceneEl.querySelector('.lxry-cinema__bg-wrap');
      var content = sceneEl.querySelector('.lxry-cinema__content');
      if (!bg) return;
      var baseFilter =
        getComputedStyle(sceneEl).getPropertyValue('--scene-img-filter') || '';

      if (state === 'past') {
        sceneEl.style.opacity = '0';
        bg.style.transform =
          'scale(' + (cfg.initZoom + cfg.zoom).toFixed(3) + ')';
        bg.style.filter =
          baseFilter +
          ' blur(' +
          cfg.blurOut +
          'px) brightness(' +
          (1 - cfg.darkenOut).toFixed(3) +
          ')';
        bg.style.opacity = '0';
        if (content) content.style.opacity = '0';
        sceneEl.style.setProperty('--_scroll-progress', '1');
      } else {
        // future
        sceneEl.style.opacity = '0';
        bg.style.transform = 'scale(' + cfg.initZoom + ')';
        bg.style.filter = baseFilter;
        bg.style.opacity = '1';
        if (content) content.style.opacity = '0';
        sceneEl.style.setProperty('--_scroll-progress', '0');
      }
    }

    // Outgoing: die aktive Szene verlässt die Bühne. Transition-Typ
    // entscheidet wie.
    function applyOutgoing(sceneOut, cfg, p) {
      var bg = sceneOut.querySelector('.lxry-cinema__bg-wrap');
      var content = sceneOut.querySelector('.lxry-cinema__content');
      if (!bg) return;

      var zoomT = clamp(p / Math.max(0.001, cfg.tZoomEnd), 0, 1);
      var eased = easeFn(cfg.easeCurve, zoomT);
      var fadeOutT = clamp(
        (p - cfg.tOutStart) / Math.max(0.001, cfg.tOutEnd - cfg.tOutStart),
        0,
        1
      );
      var slideT = easeFn(
        cfg.easeCurve,
        clamp(
          (p - cfg.tNextStart) / Math.max(0.001, cfg.tNextEnd - cfg.tNextStart),
          0,
          1
        )
      );

      var baseFilter =
        getComputedStyle(sceneOut).getPropertyValue('--scene-img-filter') || '';

      sceneOut.style.opacity = '1';
      bg.style.opacity = '1';

      switch (cfg.transition) {
        case 'crossfade': {
          bg.style.transform = 'scale(' + cfg.initZoom + ')';
          bg.style.filter =
            baseFilter + ' blur(' + (cfg.blurOut * 0.25 * slideT).toFixed(2) + 'px)';
          bg.style.opacity = (1 - slideT).toFixed(3);
          sceneOut.style.opacity = (1 - slideT).toFixed(3);
          break;
        }
        case 'slide-up': {
          bg.style.transform =
            'scale(' +
            cfg.initZoom +
            ') translateY(' +
            (-slideT * 100).toFixed(2) +
            'vh)';
          bg.style.filter =
            baseFilter + ' blur(' + (cfg.blurOut * 0.3 * slideT).toFixed(2) + 'px)';
          break;
        }
        case 'slide-left': {
          bg.style.transform =
            'scale(' +
            cfg.initZoom +
            ') translateX(' +
            (-slideT * 100).toFixed(2) +
            'vw)';
          bg.style.filter =
            baseFilter + ' blur(' + (cfg.blurOut * 0.3 * slideT).toFixed(2) + 'px)';
          break;
        }
        case 'scale-down': {
          var sdT = easeFn(
            cfg.easeCurve,
            clamp(
              (p - cfg.tOutStart) /
                Math.max(0.001, cfg.tNextEnd - cfg.tOutStart),
              0,
              1
            )
          );
          bg.style.transform =
            'scale(' + (cfg.initZoom * (1 - sdT * 0.55)).toFixed(3) + ')';
          bg.style.filter =
            baseFilter + ' blur(' + (cfg.blurOut * sdT).toFixed(2) + 'px)';
          bg.style.opacity = (1 - sdT * 0.3).toFixed(3);
          break;
        }
        case 'none': {
          bg.style.transform = 'scale(' + cfg.initZoom + ')';
          bg.style.filter = baseFilter;
          var cut = p < 0.5 ? '1' : '0';
          bg.style.opacity = cut;
          sceneOut.style.opacity = cut;
          break;
        }
        case 'zoom-through':
        default: {
          var scale = cfg.initZoom + cfg.zoom * eased;
          var blur = cfg.blurOut * eased;
          var fadeTail = 1 - clamp((p - 0.55) / 0.45, 0, 1);
          var bright = 1 - cfg.darkenOut * zoomT;
          bg.style.transform = 'scale(' + scale.toFixed(3) + ')';
          bg.style.filter =
            baseFilter +
            ' blur(' +
            blur.toFixed(2) +
            'px) brightness(' +
            bright.toFixed(3) +
            ')';
          bg.style.opacity = fadeTail.toFixed(3);
          break;
        }
      }

      // Content-Animation: null-safe, Kurve variiert pro Transition-Typ
      if (content) {
        if (cfg.transition === 'crossfade' || cfg.transition === 'none') {
          content.style.opacity = (1 - slideT).toFixed(3);
          content.style.transform = 'translate(0,0) scale(1)';
          content.style.filter = '';
        } else if (cfg.transition === 'slide-up') {
          content.style.opacity = (1 - slideT).toFixed(3);
          content.style.transform = 'translateY(' + (-slideT * 80).toFixed(1) + 'px)';
          content.style.filter = '';
        } else if (cfg.transition === 'slide-left') {
          content.style.opacity = (1 - slideT).toFixed(3);
          content.style.transform = 'translateX(' + (-slideT * 80).toFixed(1) + 'px)';
          content.style.filter = '';
        } else if (cfg.transition === 'scale-down') {
          var sdT2 = easeFn(
            cfg.easeCurve,
            clamp(
              (p - cfg.tOutStart) /
                Math.max(0.001, cfg.tNextEnd - cfg.tOutStart),
              0,
              1
            )
          );
          content.style.opacity = (1 - sdT2).toFixed(3);
          content.style.transform = 'scale(' + (1 - sdT2 * 0.4).toFixed(3) + ')';
          content.style.filter = '';
        } else {
          // zoom-through default
          var fo = easeFn(cfg.easeCurve, fadeOutT);
          content.style.opacity = (1 - fo).toFixed(3);
          content.style.transform =
            'translateY(' +
            (-fo * 30).toFixed(1) +
            'px) scale(' +
            (1 + fo * 0.3).toFixed(3) +
            ')';
          content.style.filter = 'blur(' + (fo * 4).toFixed(2) + 'px)';
        }
      }

      sceneOut.style.setProperty('--_scroll-progress', p.toFixed(3));
    }

    // Incoming: die nächste Szene fadet rein. Der Transition-Typ ist der
    // der OUTGOING-Szene (die regiert den Übergang zur nächsten).
    function applyIncoming(sceneIn, cfgPrev, p) {
      var bg = sceneIn.querySelector('.lxry-cinema__bg-wrap');
      var content = sceneIn.querySelector('.lxry-cinema__content');
      var idx = scenes.indexOf(sceneIn);
      var cfgIn = configs[idx];
      if (!bg || !cfgIn) return;

      var inStart = cfgPrev.tNextStart;
      var inEnd = cfgPrev.tNextEnd;
      var inT = clamp(
        (p - inStart) / Math.max(0.001, inEnd - inStart),
        0,
        1
      );
      var eased = easeFn(cfgPrev.easeCurve, inT);
      var baseFilter =
        getComputedStyle(sceneIn).getPropertyValue('--scene-img-filter') || '';

      bg.style.opacity = '1';

      switch (cfgPrev.transition) {
        case 'crossfade': {
          bg.style.transform = 'scale(' + cfgIn.initZoom + ')';
          bg.style.filter = baseFilter;
          bg.style.opacity = eased.toFixed(3);
          sceneIn.style.opacity = eased.toFixed(3);
          break;
        }
        case 'slide-up': {
          bg.style.transform =
            'scale(' +
            cfgIn.initZoom +
            ') translateY(' +
            ((1 - eased) * 100).toFixed(2) +
            'vh)';
          bg.style.filter = baseFilter;
          sceneIn.style.opacity = inT > 0 ? '1' : '0';
          break;
        }
        case 'slide-left': {
          bg.style.transform =
            'scale(' +
            cfgIn.initZoom +
            ') translateX(' +
            ((1 - eased) * 100).toFixed(2) +
            'vw)';
          bg.style.filter = baseFilter;
          sceneIn.style.opacity = inT > 0 ? '1' : '0';
          break;
        }
        case 'scale-down': {
          bg.style.transform =
            'scale(' +
            (cfgIn.initZoom * (1 + (1 - eased) * 0.15)).toFixed(3) +
            ')';
          bg.style.filter = baseFilter + ' blur(' + ((1 - eased) * 4).toFixed(2) + 'px)';
          sceneIn.style.opacity = eased.toFixed(3);
          break;
        }
        case 'none': {
          bg.style.transform = 'scale(' + cfgIn.initZoom + ')';
          bg.style.filter = baseFilter;
          sceneIn.style.opacity = p >= 0.5 ? '1' : '0';
          bg.style.opacity = p >= 0.5 ? '1' : '0';
          break;
        }
        case 'zoom-through':
        default: {
          var inScale = Math.max(
            cfgIn.initZoom,
            2.4 - eased * (2.4 - cfgIn.initZoom)
          );
          var inBlur = (1 - eased) * 8;
          sceneIn.style.opacity = eased.toFixed(3);
          bg.style.transform = 'scale(' + inScale.toFixed(3) + ')';
          bg.style.filter =
            baseFilter + ' blur(' + inBlur.toFixed(2) + 'px)';
          break;
        }
      }

      // Content-Fade-In (verzögert gegenüber dem Bild — wirkt cinematic)
      if (content) {
        var textStart = inStart + 0.25 * (inEnd - inStart);
        var textIn = clamp(
          (p - textStart) / Math.max(0.001, (inEnd - inStart) * 0.75),
          0,
          1
        );
        var textEased = easeFn(cfgPrev.easeCurve, textIn);
        content.style.opacity = textEased.toFixed(3);
        content.style.transform =
          'translateY(' + ((1 - textEased) * 60).toFixed(1) + 'px)';
        content.style.filter = '';
      }
    }

    var ticking = false;
    var lastCurrent = -1;

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }

    function onResize() {
      recompute();
      update();
    }

    function update() {
      var y = window.scrollY;
      var vh = window.innerHeight;
      var stackTop = stack.getBoundingClientRect().top + y;
      var totalPx = totalVh * vh / 100;

      var scrolled = y - stackTop;

      // currentIdx via walk über cumulative Offsets (piecewise per-scene vh)
      var currentIdx = 0;
      for (var i = sceneCount - 1; i >= 0; i--) {
        var offPx = sceneOffsetsVh[i] * vh / 100;
        if (scrolled >= offPx) {
          currentIdx = i;
          break;
        }
      }
      currentIdx = clamp(currentIdx, 0, sceneCount - 1);

      var sceneStartPx = sceneOffsetsVh[currentIdx] * vh / 100;
      var sceneSpanVh =
        currentIdx + 1 < sceneCount
          ? sceneOffsetsVh[currentIdx + 1] - sceneOffsetsVh[currentIdx]
          : configs[currentIdx].sceneVh;
      var sceneSpanPx = Math.max(1, sceneSpanVh * vh / 100);
      var localProgress = clamp(
        (scrolled - sceneStartPx) / sceneSpanPx,
        0,
        1
      );

      // Entrance für neu aktive Szene (einmalig pro Szene, Szene 0 skipped)
      if (currentIdx !== lastCurrent && currentIdx > 0 && !entered[currentIdx]) {
        entered[currentIdx] = true;
        runEntrance(scenes[currentIdx], configs[currentIdx]);
      }
      lastCurrent = currentIdx;

      // Active dot
      if (dots.length) {
        var activeIdx =
          localProgress > 0.5 && currentIdx + 1 < sceneCount
            ? currentIdx + 1
            : currentIdx;
        dots.forEach(function (d, i) {
          d.classList.toggle('active', i === activeIdx);
        });
      }

      // Past + future scenes zurücksetzen
      for (var j = 0; j < sceneCount; j++) {
        if (j < currentIdx) resetScene(scenes[j], 'past');
        else if (j > currentIdx + 1) resetScene(scenes[j], 'future');
      }

      // Outgoing (active)
      applyOutgoing(scenes[currentIdx], configs[currentIdx], localProgress);

      // Incoming (next) — nutzt Transition-Config der OUTGOING-Szene
      if (currentIdx + 1 < sceneCount) {
        applyIncoming(
          scenes[currentIdx + 1],
          configs[currentIdx],
          localProgress
        );
      }

      // Flash / Grain — nur bei zoom-through UND pro-Szene aktiv
      var cfgActive = configs[currentIdx];
      var isZoomThrough = cfgActive.transition === 'zoom-through';
      if (flash) {
        if (isZoomThrough && cfgActive.flash) {
          var flashProgress =
            localProgress < 0.55
              ? clamp((localProgress - 0.4) / 0.15, 0, 1)
              : clamp(1 - (localProgress - 0.55) / 0.15, 0, 1);
          flash.style.opacity = (easeInOut(flashProgress) * 0.9).toFixed(3);
        } else {
          flash.style.opacity = '0';
        }
      }
      if (grainLines) {
        if (isZoomThrough && cfgActive.grain) {
          var grainProgress =
            localProgress < 0.55
              ? clamp((localProgress - 0.35) / 0.2, 0, 1)
              : clamp(1 - (localProgress - 0.55) / 0.15, 0, 1);
          grainLines.style.opacity = easeInOut(grainProgress).toFixed(3);
          grainLines.style.transform =
            'scaleX(' + (1 + grainProgress * 8).toFixed(2) + ')';
        } else {
          grainLines.style.opacity = '0';
          grainLines.style.transform = 'scaleX(1)';
        }
      }

      // Sticky CTA: ab 80% Viewport-Scroll einblenden
      if (stickySample) {
        // Sticky CTA erst sichtbar wenn Hero-Section vollständig durchgescrollt
        var heroBottom = sectionEl.getBoundingClientRect().bottom;
        if (heroBottom < 0) stickySample.classList.add('visible');
        else stickySample.classList.remove('visible');
      }

      // Scene-Nav: am Stack-Ende ausblenden
      if (sceneNav) {
        if (y > stackTop + totalPx - vh * 0.3)
          sceneNav.classList.add('hidden');
        else sceneNav.classList.remove('hidden');
      }

      ticking = false;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      scenes.forEach(function(sceneEl) {
        var bg = sceneEl.querySelector('.lxry-cinema__bg-wrap');
        var content = sceneEl.querySelector('.lxry-cinema__content');
        if (bg) { bg.style.transform = 'scale(1)'; bg.style.filter = 'none'; bg.style.opacity = '1'; }
        if (content) { content.style.opacity = '1'; content.style.transform = 'none'; }
        sceneEl.style.opacity = '1';
      });
      return;
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    update();

    stack.__lxryCinemaCleanup = function () {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (sceneNav) sceneNav.innerHTML = '';
      stack.__lxryCinemaInit = false;
    };
  }

  function initAll(root) {
    var scope = root || document;
    scope.querySelectorAll('.lxry-cinema__stack').forEach(initCinemaHero);
  }

  // Initial boot — deferred to idle to avoid TBT spike from forced layout in recompute()
  function bootHero() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(function () { initAll(); }, { timeout: 1500 });
    } else {
      setTimeout(initAll, 100);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootHero);
  } else {
    bootHero();
  }

  // Shopify designMode: Section-Load / -Unload / Block-Select
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      initAll(e.target);
    });

    document.addEventListener('shopify:section:unload', function (e) {
      var stacks = e.target.querySelectorAll('.lxry-cinema__stack');
      Array.prototype.forEach.call(stacks, function (stack) {
        if (typeof stack.__lxryCinemaCleanup === 'function') {
          stack.__lxryCinemaCleanup();
        }
      });
    });

    document.addEventListener('shopify:block:select', function (e) {
      var block = e.target;
      if (!block) return;
      var sceneEl =
        block.classList && block.classList.contains('lxry-cinema__scene')
          ? block
          : block.querySelector && block.querySelector('.lxry-cinema__scene');
      if (!sceneEl) return;
      var stack = sceneEl.closest('.lxry-cinema__stack');
      if (!stack) return;
      var scenes = Array.prototype.slice.call(
        stack.querySelectorAll('.lxry-cinema__scene')
      );
      var idx = scenes.indexOf(sceneEl);
      if (idx < 0) return;
      // Summe der per-Szenen-vh bis zum Ziel-Block
      var vhSum = 0;
      for (var i = 0; i < idx; i++) {
        var v =
          parseFloat(
            getComputedStyle(scenes[i]).getPropertyValue('--scene-vh')
          ) || 110;
        vhSum += v;
      }
      var stackTop = stack.getBoundingClientRect().top + window.scrollY;
      var offsetPx = vhSum * window.innerHeight / 100;
      window.scrollTo({
        top: stackTop + offsetPx,
        behavior: 'smooth'
      });
    });
  }
})();
